import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Trash2, Calendar, UserRound, Clock, Plus, Edit, Bell, MapPin, Store } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TURKEY_TOPO_JSON = "https://raw.githubusercontent.com/cihadturhan/tr-geojson/master/geo/tr-cities-utf8.json";

const Biovics = () => {
  const [hospitals, setHospitals] = useState([]);
  const [biovicsDeliveries, setBiovicsDeliveries] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cityDealerMap, setCityDealerMap] = useState({});
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [selectedMapCity, setSelectedMapCity] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => (new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear().toString());
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [isEditMeetingDialogOpen, setIsEditMeetingDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [formData, setFormData] = useState({
    hospital_id: "",
    product_id: "",
    product_name: "",
    quantity: 0,
    unit_price: 0,
    delivery_date: "",
    notes: "",
  });
  const [meetingFormData, setMeetingFormData] = useState({
    hospital_id: "",
    doctor_name: "",
    meeting_date: "",
    meeting_time: "",
    follow_up_date: "",
    result: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [hospitalsRes, biovicsRes, meetingsRes, dealersRes, cityMapRes, productsRes] = await Promise.all([
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/biovics-deliveries`),
        axios.get(`${API}/meetings`),
        axios.get(`${API}/dealers`),
        axios.get(`${API}/city-dealer-map`),
        axios.get(`${API}/products?category=sternafix`),
      ]);

      setHospitals(hospitalsRes.data);
      setBiovicsDeliveries(biovicsRes.data);
      setMeetings(meetingsRes.data);
      setDealers(dealersRes.data);
      setCityDealerMap(cityMapRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Veriler yüklenirken hata oluştu");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getHospitalName = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.name : "-";
  };

  const getHospitalCity = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.city : "-";
  };

  // Map click handler
  const handleMapClick = (geo) => {
    const cityName = geo.properties.name;
    setSelectedMapCity(cityName);
    
    // Check if this city has a dealer
    const dealerInfo = cityDealerMap[cityName];
    if (dealerInfo) {
      setSelectedDealer(dealerInfo);
    } else {
      setSelectedDealer(null);
    }
  };

  // Get fill color for map provinces
  const getMapFillColor = (geo) => {
    const cityName = geo.properties.name;
    const dealerInfo = cityDealerMap[cityName];
    
    if (selectedMapCity === cityName) {
      return "#1e40af"; // Selected city
    }
    
    if (dealerInfo) {
      return dealerInfo.color || "#3B82F6";
    }
    
    return "#e2e8f0"; // No dealer
  };

  // Filter deliveries by selected city/dealer
  const cityFilteredDeliveries = useMemo(() => {
    if (!selectedMapCity) return [];
    
    const cityHospitalIds = hospitals
      .filter(h => h.city === selectedMapCity)
      .map(h => h.id);
    
    return biovicsDeliveries.filter(d => cityHospitalIds.includes(d.hospital_id));
  }, [selectedMapCity, hospitals, biovicsDeliveries]);

  // Dealer stats from map
  const dealerSalesStats = useMemo(() => {
    if (!selectedDealer) return null;
    
    const dealerCities = dealers.find(d => d.id === selectedDealer.dealer_id)?.assigned_cities || [];
    const dealerHospitalIds = hospitals
      .filter(h => dealerCities.includes(h.city))
      .map(h => h.id);
    
    const dealerDeliveries = biovicsDeliveries.filter(d => dealerHospitalIds.includes(d.hospital_id));
    const totalQuantity = dealerDeliveries.reduce((sum, d) => sum + (d.quantity || 0), 0);
    const totalAmount = dealerDeliveries.reduce((sum, d) => sum + (d.quantity * (d.unit_price || 0)), 0);
    const invoicedCount = dealerDeliveries.filter(d => d.invoice_issued).length;
    
    return {
      deliveries: dealerDeliveries.length,
      totalQuantity,
      totalAmount,
      invoicedCount,
      pendingCount: dealerDeliveries.length - invoicedCount
    };
  }, [selectedDealer, dealers, hospitals, biovicsDeliveries]);

  const handleInvoiceToggle = async (delivery) => {
    if (delivery.invoice_issued) {
      try {
        await axios.put(`${API}/biovics-deliveries/${delivery.id}/invoice`, {
          invoice_issued: false,
          invoice_date: null,
          invoice_amount: null,
        });
        toast.success("Fatura durumu iptal edildi");
        fetchData();
      } catch (error) {
        toast.error("Fatura durumu güncellenirken hata oluştu");
      }
    } else {
      setSelectedDelivery(delivery);
      const calculatedAmount = delivery.quantity * (delivery.unit_price || 0);
      setInvoiceAmount(calculatedAmount);
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      setIsInvoiceDialogOpen(true);
    }
  };

  const handleInvoiceSubmit = async () => {
    if (!selectedDelivery) return;
    try {
      await axios.put(`${API}/biovics-deliveries/${selectedDelivery.id}/invoice`, {
        invoice_issued: true,
        invoice_date: invoiceDate,
        invoice_amount: invoiceAmount,
      });
      toast.success("Fatura kesildi olarak işaretlendi");
      setIsInvoiceDialogOpen(false);
      setSelectedDelivery(null);
      setInvoiceAmount(0);
      setInvoiceDate("");
      fetchData();
    } catch (error) {
      toast.error("Fatura durumu güncellenirken hata oluştu");
    }
  };

  const handleDeleteDelivery = async (deliveryId, deliveryInfo) => {
    if (!window.confirm(`${deliveryInfo.hospitalName} için ${deliveryInfo.productName} teslimatını silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/biovics-deliveries/${deliveryId}`);
      toast.success("Sternafix teslimatı silindi");
      fetchData();
    } catch (error) {
      toast.error("Teslimat silinirken hata oluştu");
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/biovics-deliveries`, {
        hospital_id: formData.hospital_id,
        product_name: formData.product_name,
        quantity: formData.quantity,
        unit_price: formData.unit_price,
        delivery_date: formData.delivery_date,
        notes: formData.notes,
        product_id: formData.product_id || null,
      });
      toast.success("Sternafix teslimatı eklendi");
      setIsAddDialogOpen(false);
      setFormData({ hospital_id: "", product_id: "", product_name: "", quantity: 0, unit_price: 0, delivery_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Teslimat eklenirken hata oluştu");
    }
  };

  // Meeting functions
  const handleAddMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meetings`, meetingFormData);
      toast.success("Görüşme kaydedildi");
      setIsMeetingDialogOpen(false);
      resetMeetingForm();
      fetchData();
    } catch (error) {
      toast.error("Görüşme kaydedilirken hata oluştu");
    }
  };

  const handleEditMeeting = async (e) => {
    e.preventDefault();
    if (!selectedMeeting) return;
    try {
      await axios.put(`${API}/meetings/${selectedMeeting.id}`, meetingFormData);
      toast.success("Görüşme güncellendi");
      setIsEditMeetingDialogOpen(false);
      setSelectedMeeting(null);
      resetMeetingForm();
      fetchData();
    } catch (error) {
      toast.error("Görüşme güncellenirken hata oluştu");
    }
  };

  const handleDeleteMeeting = async (meetingId, meetingInfo) => {
    if (!window.confirm(`${meetingInfo.hospitalName} - ${meetingInfo.doctorName} görüşmesini silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/meetings/${meetingId}`);
      toast.success("Görüşme silindi");
      fetchData();
    } catch (error) {
      toast.error("Görüşme silinirken hata oluştu");
    }
  };

  const openEditMeetingDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setMeetingFormData({
      hospital_id: meeting.hospital_id,
      doctor_name: meeting.doctor_name,
      meeting_date: meeting.meeting_date,
      meeting_time: meeting.meeting_time,
      follow_up_date: meeting.follow_up_date || "",
      result: meeting.result,
      notes: meeting.notes || "",
    });
    setIsEditMeetingDialogOpen(true);
  };

  const resetMeetingForm = () => {
    setMeetingFormData({ hospital_id: "", doctor_name: "", meeting_date: "", meeting_time: "", follow_up_date: "", result: "", notes: "" });
  };

  const upcomingFollowUps = useMemo(() => {
    const today = new Date();
    const threeDaysLater = new Date(today);
    threeDaysLater.setDate(today.getDate() + 3);
    return meetings.filter(meeting => {
      if (!meeting.follow_up_date) return false;
      const followUpDate = new Date(meeting.follow_up_date);
      return followUpDate >= today && followUpDate <= threeDaysLater;
    });
  }, [meetings]);

  const filteredDeliveries = biovicsDeliveries.filter(delivery => {
    if (!selectedMonth || selectedMonth === "all" || !selectedYear || selectedYear === "all") {
      if (selectedYear && selectedYear !== "all") {
        return new Date(delivery.delivery_date).getFullYear() === parseInt(selectedYear);
      }
      if (selectedMonth && selectedMonth !== "all") {
        return new Date(delivery.delivery_date).getMonth() + 1 === parseInt(selectedMonth);
      }
      return true;
    }
    const deliveryDate = new Date(delivery.delivery_date);
    return deliveryDate.getMonth() + 1 === parseInt(selectedMonth) && deliveryDate.getFullYear() === parseInt(selectedYear);
  });

  const monthInvoiced = filteredDeliveries.filter(d => d.invoice_issued);
  const monthUninvoiced = filteredDeliveries.filter(d => !d.invoice_issued);
  const invoicedAmount = monthInvoiced.reduce((sum, d) => sum + (d.invoice_amount ?? (d.quantity * (d.unit_price || 0))), 0);
  const uninvoicedAmount = monthUninvoiced.reduce((sum, d) => sum + (d.quantity * (d.unit_price || 0)), 0);
  const totalAmount = invoicedAmount + uninvoicedAmount;

  const years = [...new Set(biovicsDeliveries.map(d => new Date(d.delivery_date).getFullYear()))].sort((a, b) => b - a);
  const months = [
    { value: "1", label: "Ocak" }, { value: "2", label: "Şubat" }, { value: "3", label: "Mart" },
    { value: "4", label: "Nisan" }, { value: "5", label: "Mayıs" }, { value: "6", label: "Haziran" },
    { value: "7", label: "Temmuz" }, { value: "8", label: "Ağustos" }, { value: "9", label: "Eylül" },
    { value: "10", label: "Ekim" }, { value: "11", label: "Kasım" }, { value: "12", label: "Aralık" },
  ];

  return (
    <div className="space-y-6" data-testid="sternafix-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="sternafix-title">
            Sternafix Raporu
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Sternafix satış takibi, bayi bölgeleri ve fatura yönetimi
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} data-testid="add-sternafix-btn">
          <Package className="w-4 h-4 mr-2" />
          Yeni Teslimat Ekle
        </Button>
      </div>

      {/* Map + Dealer Info Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="sternafix-map-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Bayi Bölgeleri Haritası
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ center: [35, 39], scale: 2800 }}
              style={{ width: "100%", height: "auto" }}
              data-testid="sternafix-map"
            >
              <Geographies geography={TURKEY_TOPO_JSON}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => handleMapClick(geo)}
                      style={{
                        default: {
                          fill: getMapFillColor(geo),
                          stroke: "#94a3b8",
                          strokeWidth: 0.5,
                          outline: "none",
                        },
                        hover: {
                          fill: "#2563eb",
                          stroke: "#1e40af",
                          strokeWidth: 1,
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { fill: "#1e3a8a", outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>
            </ComposableMap>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
              {dealers.filter(d => d.is_active).map(dealer => (
                <div
                  key={dealer.id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-80"
                  onClick={() => {
                    setSelectedDealer({ dealer_id: dealer.id, dealer_name: dealer.name, color: dealer.color });
                    setSelectedMapCity(null);
                  }}
                >
                  <span className="w-4 h-4 rounded" style={{ backgroundColor: dealer.color }} />
                  <span className="font-medium">{dealer.name}</span>
                  <span className="text-xs text-muted-foreground">({dealer.assigned_cities?.length || 0} il)</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm">
                <span className="w-4 h-4 rounded bg-slate-200" />
                <span className="text-muted-foreground">Atanmamış</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dealer / City Details Panel */}
        <Card data-testid="sternafix-details-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Store className="w-5 h-5" />
              {selectedMapCity ? `${selectedMapCity} Detayları` : selectedDealer ? `${selectedDealer.dealer_name}` : "Bölge Seçin"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedMapCity && !selectedDealer ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Haritadan bir şehir veya aşağıdan bir bayi seçin</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedDealer && (
                  <div className="p-3 rounded-lg border" style={{ borderColor: selectedDealer.color + '40', backgroundColor: selectedDealer.color + '10' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: selectedDealer.color }} />
                      <span className="font-bold">{selectedDealer.dealer_name}</span>
                    </div>
                    {dealerSalesStats && (
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-2xl font-bold text-blue-600">{dealerSalesStats.deliveries}</p>
                          <p className="text-xs text-muted-foreground">Teslimat</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-2xl font-bold text-purple-600">{dealerSalesStats.totalQuantity}</p>
                          <p className="text-xs text-muted-foreground">Toplam Adet</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-lg font-bold text-green-600">{dealerSalesStats.invoicedCount}</p>
                          <p className="text-xs text-muted-foreground">Faturalanan</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-lg font-bold text-red-600">{dealerSalesStats.pendingCount}</p>
                          <p className="text-xs text-muted-foreground">Bekleyen</p>
                        </div>
                        <div className="col-span-2 text-center p-2 bg-white rounded">
                          <p className="text-xl font-bold text-emerald-600">
                            {dealerSalesStats.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                          </p>
                          <p className="text-xs text-muted-foreground">Toplam Tutar</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedMapCity && (
                  <>
                    <h4 className="font-semibold text-sm text-muted-foreground">{selectedMapCity} Satışları ({cityFilteredDeliveries.length})</h4>
                    {cityFilteredDeliveries.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">Bu şehirde Sternafix satışı bulunmuyor</p>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {cityFilteredDeliveries.map(delivery => (
                          <div key={delivery.id} className="p-3 rounded-lg bg-secondary text-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{getHospitalName(delivery.hospital_id)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {delivery.product_name} - {delivery.quantity} adet
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(delivery.delivery_date).toLocaleDateString('tr-TR')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">{(delivery.quantity * (delivery.unit_price || 0)).toLocaleString('tr-TR')} TL</p>
                                {delivery.invoice_issued ? (
                                  <span className="text-xs text-green-600 font-medium">Faturalı</span>
                                ) : (
                                  <span className="text-xs text-red-600 font-medium">Bekliyor</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sternafix Invoice Report */}
      <Card data-testid="sternafix-invoice-report">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle>Sternafix Fatura Raporu</CardTitle>
            <div className="flex gap-3">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32" data-testid="year-filter">
                  <SelectValue placeholder="Yıl" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Yıllar</SelectItem>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32" data-testid="month-filter">
                  <SelectValue placeholder="Ay" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Aylar</SelectItem>
                  {months.map(month => (
                    <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 font-medium">Toplam Tutar</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  {totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                </p>
                <p className="text-xs text-blue-600 mt-1">{filteredDeliveries.length} teslimat</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm text-green-600 font-medium">Kesilen Fatura</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {invoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                </p>
                <p className="text-xs text-green-600 mt-1">{monthInvoiced.length} teslimat</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-sm text-red-600 font-medium">Bekleyen Fatura</p>
                <p className="text-2xl font-bold text-red-700 mt-1">
                  {uninvoicedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                </p>
                <p className="text-xs text-red-600 mt-1">{monthUninvoiced.length} teslimat</p>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sternafix-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hastane</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Şehir</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Ürün Adı</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Adet</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Birim Fiyat</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Toplam</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Teslim Tarihi</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Fatura Durumu</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeliveries
                  .sort((a, b) => {
                    if (a.invoice_issued !== b.invoice_issued) return a.invoice_issued ? 1 : -1;
                    return new Date(b.delivery_date) - new Date(a.delivery_date);
                  })
                  .map((delivery) => {
                    const totalPrice = delivery.quantity * (delivery.unit_price || 0);
                    return (
                      <tr
                        key={delivery.id}
                        className={`border-b border-border table-row ${!delivery.invoice_issued ? 'bg-red-50' : ''}`}
                        data-testid={`sternafix-row-${delivery.id}`}
                      >
                        <td className="py-3 px-4 font-medium">{getHospitalName(delivery.hospital_id)}</td>
                        <td className="py-3 px-4 text-muted-foreground">{getHospitalCity(delivery.hospital_id)}</td>
                        <td className="py-3 px-4">{delivery.product_name}</td>
                        <td className="py-3 px-4 text-center font-semibold text-purple-600">{delivery.quantity}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground">
                          {(delivery.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          {totalPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {new Date(delivery.delivery_date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {delivery.invoice_issued ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Fatura Kesildi</span>
                              {delivery.invoice_date && (
                                <span className="text-xs text-muted-foreground mt-1">{new Date(delivery.invoice_date).toLocaleDateString('tr-TR')}</span>
                              )}
                              {delivery.invoice_amount != null && (
                                <span className="text-xs font-bold text-green-700 mt-1">{delivery.invoice_amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">Bekliyor</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant={delivery.invoice_issued ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleInvoiceToggle(delivery)}
                              data-testid={`toggle-invoice-${delivery.id}`}
                              className={delivery.invoice_issued ? "" : "bg-green-600 hover:bg-green-700 text-white"}
                            >
                              {delivery.invoice_issued ? "İptal Et" : "Fatura Kesildi"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDelivery(delivery.id, { hospitalName: getHospitalName(delivery.hospital_id), productName: delivery.product_name })}
                              data-testid={`delete-delivery-${delivery.id}`}
                              className="text-destructive hover:text-destructive hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {filteredDeliveries.length === 0 && (
              <div className="text-center py-12" data-testid="no-sternafix-data">
                <p className="text-muted-foreground">
                  {selectedMonth && selectedYear ? "Seçilen dönem için Sternafix teslimatı bulunmamaktadır" : "Sternafix teslimatı bulunmamaktadır"}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>Fatura Bilgileri</DialogTitle>
          </DialogHeader>
          {selectedDelivery && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-sm font-medium text-muted-foreground">Hastane</p>
                <p className="font-semibold">{getHospitalName(selectedDelivery.hospital_id)}</p>
                <p className="text-sm text-muted-foreground mt-2">Ürün</p>
                <p className="font-medium">{selectedDelivery.product_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Adet</Label>
                  <Input value={selectedDelivery.quantity} disabled className="font-semibold" />
                </div>
                <div>
                  <Label>Birim Fiyat</Label>
                  <Input value={(selectedDelivery.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL'} disabled className="font-semibold" />
                </div>
              </div>
              <div className="border-t pt-4">
                <Label htmlFor="invoice_amount">Fatura Tutarı (TL) *</Label>
                <Input
                  id="invoice_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="invoice-amount-input"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(parseFloat(e.target.value) || 0)}
                  className="mt-2 font-bold text-lg"
                  autoFocus
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => { setIsInvoiceDialogOpen(false); setSelectedDelivery(null); setInvoiceAmount(0); }} data-testid="cancel-invoice-btn">İptal</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleInvoiceSubmit} data-testid="confirm-invoice-btn">Fatura Kes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Sternafix Delivery Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-sternafix-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Sternafix Teslimatı Ekle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="hospital_id">Hastane *</Label>
                <Select value={formData.hospital_id} onValueChange={(value) => setFormData({ ...formData, hospital_id: value })} required>
                  <SelectTrigger data-testid="sternafix-hospital-select">
                    <SelectValue placeholder="Hastane seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitals.map((hospital) => (
                      <SelectItem key={hospital.id} value={hospital.id}>{hospital.name} - {hospital.city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Ürün Seçin (Envanterden)</Label>
                <Select value={formData.product_id} onValueChange={(value) => {
                  if (value === "manual") {
                    setFormData({ ...formData, product_id: "", product_name: "", unit_price: 0 });
                  } else {
                    const product = products.find(p => p.id === value);
                    if (product) {
                      setFormData({ ...formData, product_id: product.id, product_name: product.name, unit_price: product.unit_price });
                    }
                  }
                }}>
                  <SelectTrigger data-testid="sternafix-product-select">
                    <SelectValue placeholder="Envanterden ürün seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuel Giriş</SelectItem>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} (Stok: {product.stock_quantity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!formData.product_id && (
                <div className="col-span-2">
                  <Label htmlFor="product_name">Ürün Adı (Manuel) *</Label>
                  <Input id="product_name" data-testid="sternafix-product-input" placeholder="Örn: Sternafix 3.0" value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} required />
                </div>
              )}
              {formData.product_id && (
                <div className="col-span-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                  <span className="text-blue-700 font-medium">Seçilen: {formData.product_name}</span>
                  <span className="text-blue-600 ml-2">(Stoktan otomatik düşülecek)</span>
                </div>
              )}
              <div>
                <Label htmlFor="quantity">Adet *</Label>
                <Input id="quantity" type="number" min="1" data-testid="sternafix-quantity-input" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} required />
              </div>
              <div>
                <Label htmlFor="unit_price">Birim Fiyat (TL) *</Label>
                <Input id="unit_price" type="number" min="0" step="0.01" data-testid="sternafix-price-input" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} required />
              </div>
              <div>
                <Label htmlFor="delivery_date">Teslim Tarihi *</Label>
                <Input id="delivery_date" type="date" data-testid="sternafix-date-input" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} required />
              </div>
              <div>
                <Label>Toplam Tutar</Label>
                <Input value={(formData.quantity * formData.unit_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL'} disabled className="font-bold text-green-600" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notlar</Label>
                <Input id="notes" data-testid="sternafix-notes-input" placeholder="Ek bilgiler..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setIsAddDialogOpen(false); setFormData({ hospital_id: "", product_id: "", product_name: "", quantity: 0, unit_price: 0, delivery_date: "", notes: "" }); }} data-testid="cancel-add-btn">İptal</Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-add-btn">Ekle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Meeting Tracking Section */}
      <Card data-testid="meeting-tracking-section">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CardTitle className="flex items-center gap-2">
                <UserRound className="w-5 h-5" />
                Görüşme Takibi
              </CardTitle>
              {upcomingFollowUps.length > 0 && (
                <span className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                  <Bell className="w-4 h-4" />
                  {upcomingFollowUps.length} yaklaşan takip
                </span>
              )}
            </div>
            <Button onClick={() => setIsMeetingDialogOpen(true)} data-testid="add-meeting-btn" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Görüşme Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingFollowUps.length > 0 && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="upcoming-followups-alert">
              <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Yaklaşan Takip Tarihleri
              </h4>
              <div className="space-y-2">
                {upcomingFollowUps.map((meeting) => (
                  <div key={meeting.id} className="flex items-center justify-between text-sm">
                    <span><span className="font-medium">{getHospitalName(meeting.hospital_id)}</span> - {meeting.doctor_name}</span>
                    <span className="text-orange-700 font-medium">{new Date(meeting.follow_up_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <p className="text-sm text-purple-600 font-medium">Toplam Görüşme</p>
                <p className="text-2xl font-bold text-purple-700 mt-1">{meetings.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600 font-medium">Bu Ay</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  {meetings.filter(m => { const d = new Date(m.meeting_date); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <p className="text-sm text-orange-600 font-medium">Bekleyen Takip</p>
                <p className="text-2xl font-bold text-orange-700 mt-1">
                  {meetings.filter(m => m.follow_up_date && new Date(m.follow_up_date) >= new Date()).length}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="meetings-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hastane</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Hekim</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Tarih</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Saat</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Sonuç</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Takip Tarihi</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {meetings.sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date)).map((meeting) => {
                  const isFollowUpSoon = upcomingFollowUps.some(m => m.id === meeting.id);
                  return (
                    <tr key={meeting.id} className={`border-b border-border table-row ${isFollowUpSoon ? 'bg-orange-50' : ''}`} data-testid={`meeting-row-${meeting.id}`}>
                      <td className="py-3 px-4 font-medium">{getHospitalName(meeting.hospital_id)}</td>
                      <td className="py-3 px-4"><div className="flex items-center gap-2"><UserRound className="w-4 h-4 text-muted-foreground" />{meeting.doctor_name}</div></td>
                      <td className="py-3 px-4 text-muted-foreground">{new Date(meeting.meeting_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</td>
                      <td className="py-3 px-4"><div className="flex items-center gap-1 text-muted-foreground"><Clock className="w-3 h-3" />{meeting.meeting_time}</div></td>
                      <td className="py-3 px-4"><span className="text-sm">{meeting.result}</span>{meeting.notes && <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]" title={meeting.notes}>{meeting.notes}</p>}</td>
                      <td className="py-3 px-4">
                        {meeting.follow_up_date ? (
                          <div className={`flex items-center gap-1 ${isFollowUpSoon ? 'text-orange-600 font-medium' : 'text-muted-foreground'}`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(meeting.follow_up_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {isFollowUpSoon && <Bell className="w-3 h-3 ml-1" />}
                          </div>
                        ) : <span className="text-muted-foreground text-sm">-</span>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditMeetingDialog(meeting)} data-testid={`edit-meeting-${meeting.id}`} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMeeting(meeting.id, { hospitalName: getHospitalName(meeting.hospital_id), doctorName: meeting.doctor_name })} data-testid={`delete-meeting-${meeting.id}`} className="text-destructive hover:text-destructive hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {meetings.length === 0 && (
              <div className="text-center py-12" data-testid="no-meetings">
                <UserRound className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz görüşme kaydı bulunmamaktadır</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Meeting Dialog */}
      <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-meeting-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserRound className="w-5 h-5" />Yeni Görüşme Ekle</DialogTitle></DialogHeader>
          <form onSubmit={handleAddMeeting} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Hastane *</Label>
                <Select value={meetingFormData.hospital_id} onValueChange={(value) => setMeetingFormData({ ...meetingFormData, hospital_id: value })} required>
                  <SelectTrigger data-testid="meeting-hospital-select"><SelectValue placeholder="Hastane seçin" /></SelectTrigger>
                  <SelectContent>{hospitals.map(h => <SelectItem key={h.id} value={h.id}>{h.name} - {h.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Hekim Adı *</Label>
                <Input data-testid="meeting-doctor-input" placeholder="Dr. ..." value={meetingFormData.doctor_name} onChange={(e) => setMeetingFormData({ ...meetingFormData, doctor_name: e.target.value })} required />
              </div>
              <div>
                <Label>Görüşme Tarihi *</Label>
                <Input type="date" data-testid="meeting-date-input" value={meetingFormData.meeting_date} onChange={(e) => setMeetingFormData({ ...meetingFormData, meeting_date: e.target.value })} required />
              </div>
              <div>
                <Label>Görüşme Saati *</Label>
                <Input type="time" data-testid="meeting-time-input" value={meetingFormData.meeting_time} onChange={(e) => setMeetingFormData({ ...meetingFormData, meeting_time: e.target.value })} required />
              </div>
              <div className="col-span-2">
                <Label>Sonuç *</Label>
                <Input data-testid="meeting-result-input" placeholder="Örn: Olumlu..." value={meetingFormData.result} onChange={(e) => setMeetingFormData({ ...meetingFormData, result: e.target.value })} required />
              </div>
              <div>
                <Label>Takip Tarihi</Label>
                <Input type="date" data-testid="meeting-followup-input" value={meetingFormData.follow_up_date} onChange={(e) => setMeetingFormData({ ...meetingFormData, follow_up_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Notlar</Label>
                <Textarea data-testid="meeting-notes-input" placeholder="Görüşme detayları..." value={meetingFormData.notes} onChange={(e) => setMeetingFormData({ ...meetingFormData, notes: e.target.value })} rows={3} />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setIsMeetingDialogOpen(false); resetMeetingForm(); }} data-testid="cancel-meeting-btn">İptal</Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="confirm-meeting-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Meeting Dialog */}
      <Dialog open={isEditMeetingDialogOpen} onOpenChange={setIsEditMeetingDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-meeting-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5" />Görüşmeyi Düzenle</DialogTitle></DialogHeader>
          <form onSubmit={handleEditMeeting} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Hastane *</Label>
                <Select value={meetingFormData.hospital_id} onValueChange={(value) => setMeetingFormData({ ...meetingFormData, hospital_id: value })} required>
                  <SelectTrigger data-testid="edit-meeting-hospital-select"><SelectValue placeholder="Hastane seçin" /></SelectTrigger>
                  <SelectContent>{hospitals.map(h => <SelectItem key={h.id} value={h.id}>{h.name} - {h.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Hekim Adı *</Label>
                <Input data-testid="edit-meeting-doctor-input" value={meetingFormData.doctor_name} onChange={(e) => setMeetingFormData({ ...meetingFormData, doctor_name: e.target.value })} required />
              </div>
              <div>
                <Label>Görüşme Tarihi *</Label>
                <Input type="date" data-testid="edit-meeting-date-input" value={meetingFormData.meeting_date} onChange={(e) => setMeetingFormData({ ...meetingFormData, meeting_date: e.target.value })} required />
              </div>
              <div>
                <Label>Görüşme Saati *</Label>
                <Input type="time" data-testid="edit-meeting-time-input" value={meetingFormData.meeting_time} onChange={(e) => setMeetingFormData({ ...meetingFormData, meeting_time: e.target.value })} required />
              </div>
              <div className="col-span-2">
                <Label>Sonuç *</Label>
                <Input data-testid="edit-meeting-result-input" value={meetingFormData.result} onChange={(e) => setMeetingFormData({ ...meetingFormData, result: e.target.value })} required />
              </div>
              <div>
                <Label>Takip Tarihi</Label>
                <Input type="date" data-testid="edit-meeting-followup-input" value={meetingFormData.follow_up_date} onChange={(e) => setMeetingFormData({ ...meetingFormData, follow_up_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Notlar</Label>
                <Textarea data-testid="edit-meeting-notes-input" value={meetingFormData.notes} onChange={(e) => setMeetingFormData({ ...meetingFormData, notes: e.target.value })} rows={3} />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setIsEditMeetingDialogOpen(false); setSelectedMeeting(null); resetMeetingForm(); }} data-testid="cancel-edit-meeting-btn">İptal</Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="confirm-edit-meeting-btn">Güncelle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Biovics;
